import React, { useCallback, useEffect } from "react";

import { faGear } from "@fortawesome/pro-solid-svg-icons/faGear";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";
import { Button } from "@mapmycustomers/ui";

import { skipNonListViewFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import Search from "@app/scene/dashboard/components/Search";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import useDrillDownView from "../util/useDrillDownView";

import styles from "./Actions.module.scss";
import ExportButton from "./ExportButton";
import SelectColumnsButton from "./SelectColumnsButton";

export interface HeaderProps {
  onEdit?: () => void;
  onExport?: () => void;
  showSelectColumnsButton?: boolean;
}

const Actions = ({ onEdit, onExport, showSelectColumnsButton = true }: HeaderProps) => {
  const intl = useIntl();
  const { updateFilterModel, viewState } = useDrillDownView();

  const handleQueryChange = useCallback(
    (query: string) => {
      query = query.trim().toLowerCase();
      if (query) {
        updateFilterModel({ name: { operator: FilterOperator.CONTAINS, value: query } });
      } else {
        updateFilterModel({ name: undefined });
      }
    },
    [updateFilterModel]
  );
  const [innerQuery, setInnerQuery, resetInnerQuery] = useStateWithDebouncedListener(
    [handleQueryChange, 500],
    ""
  );
  useEffect(() => {
    if ("name" in viewState.filter && isSimpleCondition(viewState.filter.name)) {
      resetInnerQuery(viewState.filter.name.value);
    }
  }, [viewState.filter, resetInnerQuery]);

  return (
    <div className={styles.container}>
      <Search onChange={setInnerQuery} value={innerQuery} />
      {onEdit && (
        <Tooltip
          placement="bottom"
          title={intl.formatMessage({
            id: "dashboard.drillDown.action.edit",
            defaultMessage: "Edit Chart",
            description: "Edit Chart option in the DrillDown header on the Dashboard page",
          })}
        >
          <Button icon={<FontAwesomeIcon icon={faGear} />} iconOnly onClick={onEdit} type="text" />
        </Tooltip>
      )}
      {showSelectColumnsButton && <SelectColumnsButton fieldFilter={skipNonListViewFields} />}
      {onExport && <ExportButton onExport={onExport} />}
    </div>
  );
};

export default Actions;
